import { Modal, type ModalConfig, type ModalReturn } from '@arco-design/web-vue'
import { assign } from 'lodash-es'
import { createVNode, defineComponent, getCurrentInstance, h, nextTick, render, shallowRef } from 'vue'

/**
 * 使用模态框的自定义 hook。
 *
 * @returns {object} 包含打开和关闭模态框的方法。
 * @property {Function} openModal - 打开模态框的方法。
 * @property {Function} closeModal - 关闭模态框的方法。
 */
export default function useModal() {
  const modal: Record<string, ModalReturn | null> = {}
  const defaultInstanceId = 'modalInstance'
  const appContext = getCurrentInstance()?.appContext
  const baseOptions = ref<Pick<ModalConfig, 'modalClass' | 'footer'>>({
    modalClass: 'stp-modal',
    footer: false,
  })

  function openModal(options: ModalConfig, modalId = defaultInstanceId): void {
    modal[modalId]?.close()
    const config = shallowRef<ModalConfig>(assign({}, baseOptions.value, options))
    const visible = ref(true)
    const container = document.createElement('div')
    container.className = 'arco-overlay arco-overlay-modal'
    let modalReturn: ModalReturn

    const cleanup = () => {
      void nextTick(() => {
        render(null, container)
        container.remove()
        if (modal[modalId] === modalReturn)
          modal[modalId] = null
      })
    }

    const host = defineComponent({
      setup() {
        return () => {
          const {
            content,
            title,
            footer,
            onClose,
            ...modalOptions
          } = config.value
          const slots = {
            default: typeof content === 'function' ? content : () => content,
            ...(typeof title === 'function' ? { title } : {}),
            ...(typeof footer === 'function' ? { footer } : {}),
          }

          return h(Modal, {
            ...modalOptions,
            'title': typeof title === 'function' ? undefined : title,
            'footer': typeof footer === 'boolean' ? footer : undefined,
            'visible': visible.value,
            'unmountOnClose': true,
            'onUpdate:visible': (value: boolean) => {
              visible.value = value
            },
            'onClose': () => {
              onClose?.()
              cleanup()
            },
          }, slots)
        }
      },
    })

    const vnode = createVNode(host)
    vnode.appContext = appContext ?? null
    render(vnode, container)
    document.body.appendChild(container)

    modalReturn = {
      close: () => {
        visible.value = false
      },
      update: (updates) => {
        config.value = assign({}, config.value, updates)
      },
    }
    modal[modalId] = modalReturn
  }

  function openWarningModal(options: ModalConfig, modalId = defaultInstanceId) {
    modal[modalId] = Modal.warning(options)
  }

  function closeModal(modalId = defaultInstanceId) {
    if (modal[modalId] && typeof modal[modalId].close === 'function') {
      modal[modalId].close()
    }
  }

  return {
    openModal,
    openWarningModal,
    closeModal,
  }
}
